/**
 * InviteUserDialog - Dialog for inviting new users
 * Extracted from SettingsPage to reduce file size
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function InviteUserDialog({
  open,
  onOpenChange,
  inviteData,
  setInviteData,
  billingPreview,
  inviting,
  onInvite,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite New User</DialogTitle>
          <DialogDescription>
            Add a new team member to your agency
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Full Name *</Label>
            <Input
              value={inviteData.name}
              onChange={(e) => setInviteData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="John Smith"
            />
          </div>
          <div className="space-y-2">
            <Label>Email *</Label>
            <Input
              type="email"
              value={inviteData.email}
              onChange={(e) => setInviteData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="john@example.com"
            />
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={inviteData.role} onValueChange={(v) => setInviteData(prev => ({ ...prev, role: v }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Administrator</SelectItem>
                <SelectItem value="case_manager">Case Manager</SelectItem>
                <SelectItem value="hr_scheduler">HR/Scheduler</SelectItem>
                <SelectItem value="evv_specialist">EVV Specialist</SelectItem>
                <SelectItem value="intake_coordinator">Intake Coordinator</SelectItem>
                <SelectItem value="receptionist">Receptionist</SelectItem>
                <SelectItem value="rn">Registered Nurse (RN)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">Note: PCWs are managed via Employees, not portal users</p>
          </div>
          <div className="space-y-2">
            <Label>Phone (optional)</Label>
            <Input
              value={inviteData.phone}
              onChange={(e) => setInviteData(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="555-123-4567"
            />
          </div>

          {/* Billing Impact Preview */}
          {billingPreview && (
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-xs font-medium text-slate-700 mb-2">Billing Impact</p>
              {billingPreview.after_adding_user.cost_increase > 0 ? (
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Current monthly</span>
                    <span>${billingPreview.current.total_monthly.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-amber-600">
                    <span>+ Additional user</span>
                    <span>+${billingPreview.additional_user_price.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-medium border-t pt-1">
                    <span>New monthly</span>
                    <span>${billingPreview.after_adding_user.total_monthly.toFixed(2)}</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-emerald-600">
                  This user is included in your plan (no additional cost)
                </p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onInvite} disabled={inviting}>
            {inviting ? "Inviting..." : "Invite User"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
